import classNames from 'classnames';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { BottomSheet } from 'react-spring-bottom-sheet';

import { useBowser, useI18n } from 'common/hooks';
import { actionGetResources } from 'common/redux/resources/actions';
import svgIcons from 'common/svgIcons';

import { getCulture } from 'utils/culture';

import SvgIconUrl from '../SvgIconUrl/SvgIconUrl.component';
import { Tooltip } from '../Tooltip';
import './LanguageSelect.css';

type Option = {
  value?: string | null;
  label?: string | null;
};

export const LanguageSelect: React.FC<React.PropsWithChildren> = React.memo(() => {
  const [enabled, toggleEnabled] = useState(false);
  const [menuIsOpen, toggle] = useState(false);
  const [language, setLanguage] = useState(getCulture());

  const dispatch = useDispatch();
  const { isMobile, isDesktop } = useBowser();
  const { translate } = useI18n();
  const languages = useSelector((state: DefaultRootState) => state.common.resources.languages);

  const options: Option[] = useMemo(
    () =>
      languages
        .map((lang) => ({ value: lang.code, label: lang.displayName }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
    [languages],
  );

  const open = useCallback(() => toggle(true), []);
  const close = useCallback(() => toggle(false), []);
  const enable = useCallback(() => toggleEnabled(true), []);

  useEffect(() => {
    toggleEnabled(menuIsOpen);
  }, [menuIsOpen]);

  const onLanguageSelect = useCallback(
    (option: Option) => {
      if (!option.value) return;

      setLanguage(option.value);
      close();

      if (option.value && option.value !== language) {
        dispatch(actionGetResources(option.value));
      }
    },
    [close, dispatch, language],
  );

  const optionClassName = useCallback(
    (isSelected: boolean) =>
      classNames({
        Body2_reg_14: true,
        language__list_item: true,
        'language__list_item--active': isSelected,
      }),
    [],
  );

  const Option = useCallback(
    (option: Option) => {
      const onClick = () => onLanguageSelect(option);
      const isSelected = language === option.value;
      return (
        <div onClick={onClick} key={option.value} className={optionClassName(isSelected)}>
          <SvgIconUrl className='language__list_item_value' symbol={svgIcons.Flag(option.value)} />
          <span className='Body2_light_14'>{option.label}</span>
          {language === option.value && <SvgIconUrl className='language__list_item_icon' symbol={svgIcons.Check()} />}
        </div>
      );
    },
    [language, onLanguageSelect, optionClassName],
  );

  const List = useMemo(
    () => (
      <div className='language__list'>
        {isDesktop && (
          <p className='language__list_title H1_med_20'>{translate.toText('mobile:language-select:title')}</p>
        )}
        {options.map(Option)}
      </div>
    ),
    [Option, isDesktop, options, translate],
  );

  const Value = useMemo(
    () => (
      <div className='language__value' onMouseOver={enable} onClick={open}>
        <SvgIconUrl className='language__value_icon' symbol={svgIcons.Flag(language)} />
        <span className='Body2_reg_14 language__value_label'>{language.toUpperCase()}</span>
      </div>
    ),
    [enable, language, open],
  );

  if (isMobile) {
    return (
      <>
        {Value}
        <BottomSheet
          className='light language__select'
          initialFocusRef={false}
          expandOnContentDrag
          onDismiss={close}
          header={<p className='language__list_title H1_med_20'>{translate.toText('mobile:language-select:title')}</p>}
          snapPoints={({ maxHeight }) => [maxHeight * 0.45, maxHeight - 35]}
          defaultSnap={({ snapPoints }) => Math.min(...snapPoints)}
          open={menuIsOpen}
        >
          {List}
        </BottomSheet>
      </>
    );
  }

  return (
    <Tooltip
      overlayClassName='light language__select'
      enabled={enabled}
      placement='topLeft'
      trigger={['click']}
      overlay={List}
      overlayStyle={{ maxHeight: '40vh', overflow: 'scroll' }}
    >
      {Value}
    </Tooltip>
  );
});
